import type { EnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import type { ResolvedAppScope } from './appScope.types';
import {
  teamSlugFromEnterprisePath,
  workspaceSlugFromEnterprisePath
} from './resolveWorkspaceSlugForLinks';
import type { TeamRef, WorkspaceRef } from './entityWorkspaceRouting';

export type ProductionCreateScopeSeed = {
  enterprise_workspace_id?: number | null;
  enterprise_team_id?: number | null;
};

export type ResolvedProductionCreateScope = {
  enterprise_workspace_id?: number;
  enterprise_team_id?: number;
  workspaces: WorkspaceRef[];
  teams: TeamRef[];
};

/**
 * Layer 2 create defaults from resolved app scope (preferred).
 * Keeps explicit IDs from the opener when present.
 */
export function productionCreateScopeIdsFromResolvedScope(
  scope: ResolvedAppScope,
  existing?: ProductionCreateScopeSeed
): ProductionCreateScopeSeed {
  const enterprise_workspace_id =
    existing?.enterprise_workspace_id != null
      ? existing.enterprise_workspace_id
      : scope.level !== 'organization'
        ? scope.workspaceId
        : null;

  const enterprise_team_id =
    existing?.enterprise_team_id != null
      ? existing.enterprise_team_id
      : scope.level === 'team'
        ? scope.teamId
        : null;

  return {
    ...(enterprise_workspace_id != null ? { enterprise_workspace_id } : {}),
    ...(enterprise_team_id != null ? { enterprise_team_id } : {})
  };
}

/**
 * @deprecated Prefer seeding create from {@link ResolvedAppScope} via
 * `appScopeService.useResolvedAppScope()` and
 * {@link productionCreateScopeIdsFromResolvedScope}, then load workspace/team
 * refs with `enterpriseService` when needed for redirects.
 *
 * This helper re-parses the URL pathname and resolves slugs asynchronously,
 * which duplicates Layer 2 outside AppScope context. It exists only because
 * legacy Redux class modals (e.g. `ProductionSetUpModal`) cannot use hooks or
 * AppScopeContext.
 */
export async function resolveProductionCreateScopeFromPath(input: {
  organizationId: string;
  pathname: string;
  existing?: ProductionCreateScopeSeed;
  enterpriseService: Pick<
    EnterpriseService,
    'ensureWorkspaces' | 'ensureTeams'
  >;
}): Promise<ResolvedProductionCreateScope> {
  const { organizationId, pathname, existing, enterpriseService } = input;
  const workspaces = await enterpriseService.ensureWorkspaces(organizationId);
  const workspaceRefs: WorkspaceRef[] = workspaces.map(workspace => ({
    id: workspace.id,
    slug: workspace.slug
  }));

  let workspaceId =
    existing?.enterprise_workspace_id != null
      ? existing.enterprise_workspace_id
      : undefined;

  if (workspaceId == null) {
    const workspaceSlug = workspaceSlugFromEnterprisePath(pathname);
    if (workspaceSlug) {
      workspaceId = workspaces.find(
        workspace => workspace.slug === workspaceSlug
      )?.id;
    }
  }

  let teamId =
    existing?.enterprise_team_id != null
      ? existing.enterprise_team_id
      : undefined;

  let teams: TeamRef[] = [];

  if (workspaceId != null) {
    const workspaceTeams = await enterpriseService.ensureTeams({
      organizationId,
      workspaceId
    });
    teams = workspaceTeams.map(team => ({
      id: team.id,
      slug: team.slug
    }));

    if (teamId == null) {
      const teamSlug = teamSlugFromEnterprisePath(pathname);
      if (teamSlug) {
        teamId = workspaceTeams.find(team => team.slug === teamSlug)?.id;
      }
    }
  }

  return {
    ...(workspaceId != null ? { enterprise_workspace_id: workspaceId } : {}),
    ...(teamId != null ? { enterprise_team_id: teamId } : {}),
    workspaces: workspaceRefs,
    teams
  };
}
